import { InfinityScroll, Loader, LogService, Media, SelectorImage, ToastHelper, User, VisionService, onUserChange } from '@hub/shared'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SubscriptionLike } from 'rxjs'
import './feed-selector.component.scoped.scss'
import './feed-selector.component.scss'

const MEDIAS_PER_PAGE = 12

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  idProfile: string | number
  socialNetwork?: 'instagram' | 'tiktok' | 'twitter' | 'youtube'
  selectedPhoto?: Media
  onSelectPhoto?: (photo: Media) => any
  name?: string
  getOnLoading?: (loading: boolean) => any
}

export default ({ idProfile, socialNetwork = 'instagram', selectedPhoto, onSelectPhoto, name = '', getOnLoading }: Props) => {
  const timestamp = `random-id-${(1 + Date.now() + Math.random()).toString().replace('.', '')}`
  const [photo, setPhoto] = useState(selectedPhoto)
  const [feed, setFeed] = useState<Media[]>([])
  const [hasNextPage, setHasNextPage] = useState(false)
  const [page, setPage] = useState(0)
  const visionService = useMemo(() => new VisionService(), [])
  const logService = useMemo(() => new LogService(), [])
  const [t] = useTranslation('photoGrid')
  const toast = new ToastHelper().toast
  const [loading, setLoading] = useState(true)
  const [loadingPagination, setLoadingPagination] = useState(false)
  const [nextUrl, setNextUrl] = useState(null)
  const onUserChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const [username, setUsername] = useState<string | undefined>('')
  logService.init('Feed Selector Component')

  const onLoadMore = useCallback(async () => {
    if (!loadingPagination && hasNextPage && username) {
      try {
        setLoadingPagination(true)
        const response = await visionService.getMediaFeed(idProfile, page, MEDIAS_PER_PAGE, username, nextUrl)
        if (response?.medias?.length) {
          const newPage = page + 1
          setPage(newPage)
          setFeed((prevFeed) => [...prevFeed, ...(response?.medias || [])])
          setHasNextPage(response?.hasNextPage)
          if (response?.nextUrl) {
            setNextUrl(response?.nextUrl)
          }
        } else {
          setHasNextPage(false)
        }
      } catch (error) {
        logService.error({ message: 'Failed to get feed', error })
        toast.error(`<strong>${t('errorFeed')}</strong>`, {
          closeButton: true,
          className: 'full',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          duration: 3600000,
          notOverClick: true,
          immediately: true,
        })
      } finally {
        setLoadingPagination(false)
      }
    }
  }, [loadingPagination, idProfile, visionService, logService, toast, t, hasNextPage, page, nextUrl, username])

  useEffect(() => {
    onUserChangeSubscription.current = onUserChange.subscribe((userChanged: User) => {
      const user = userChanged?.identities?.find((i: any) => i.id === idProfile)?.username
      setUsername(user)
    })
    return () => {
      onUserChangeSubscription.current?.unsubscribe()
    }
  }, [idProfile])

  useEffect(() => {
    if (username) {
      setFeed([])
      setHasNextPage(true)
      setPhoto(selectedPhoto)
      handleOnLoading(true)

      visionService
        .getMediaFeed(idProfile, page, MEDIAS_PER_PAGE, username, nextUrl)
        .then((response) => {
          setPage((prevPage) => prevPage + 1)
          setFeed((prevFeed) => [...prevFeed, ...(response?.medias || [])])
          setHasNextPage(response?.hasNextPage)
          if (response?.nextUrl) {
            setNextUrl(response?.nextUrl)
          }
        })
        .catch((error) => {
          logService.error({ message: 'Failed to get feed', error })
          toast.error(`<strong>${t('errorFeed')}</strong>`, {
            closeButton: true,
            className: 'full',
            position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
            duration: 3600000,
            notOverClick: true,
            immediately: true,
          })
        })
        .finally(() => handleOnLoading(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idProfile, socialNetwork, selectedPhoto, username])

  const handleSelectPhoto = useCallback(
    (newPhoto: Media) => {
      setPhoto(newPhoto)
      if (onSelectPhoto && typeof onSelectPhoto === 'function') {
        onSelectPhoto(newPhoto)
      }
    },
    [onSelectPhoto]
  )

  const handleOnLoading = useCallback(
    (newLoading: boolean) => {
      setLoading(newLoading)
      if (getOnLoading && typeof getOnLoading === 'function') {
        getOnLoading(newLoading)
      }
    },
    [getOnLoading]
  )

  if (!idProfile) {
    return null
  }

  return (
    <div className="feed-selection">
      {loading ? (
        <div className="wrapper-loader">
          <Loader />
        </div>
      ) : (
        <div className="box-wrapper-photos scrollbar" id={name || timestamp}>
          <InfinityScroll
            loading={loadingPagination}
            className="wrapper-photos-modal-select scrollbar"
            elementToScrollId={name || timestamp}
            hasMore={!!hasNextPage}
            onLoadMore={onLoadMore}
            endMessage={' '}
          >
            {feed?.map((p, i) => {
              return (
                <SelectorImage
                  index={p?.link}
                  media={p}
                  key={p?._id || i}
                  checked={p?.link === photo?.link}
                  mediaTitle={p?.usuario?.username}
                  // TODO: Remover o "p?.imagens_storage?.thumbnail?.url ||" quando o backend estiver pronto
                  // Verificar situação com o Zé
                  mediaURL={p?.imagens_storage?.thumbnail?.url || p?.imagens?.thumbnail?.url}
                  onChange={() => handleSelectPhoto(p)}
                  hasLabel={false}
                  maxHeight={true}
                />
              )
            })}
          </InfinityScroll>
        </div>
      )}
    </div>
  )
}
